<template>
  <el-dialog
    custom-class="bids-act-modal"
    :visible.sync="actDialog"
    :width="isMobile ? '95%' : '55%'"
    close-on-click-modal
    :before-close="() => $emit('close')"
    lock-scroll
  >
    <template v-if="response && response.id">
      <h1 slot="title" class="bids-act-modal__title">
        ИНФОРМАЦИИ ПО АКТУ ВЫПОЛНЕННЫХ РАБОТ
      </h1>
      <el-row v-if="response.auto" class="plates">
        <span>Авто: {{ response.auto.state_numbers || 'Н/Д' }}</span>
        <span>Прицеп: {{ response.auto.state_numbers_trailer || 'Н/Д' }}</span>
      </el-row>
      <loader-wrapper :loading="loading || loader">
        <el-row v-if="!loader && (!activeAct || !activeAct.id)" class="no-ttn">
          НЕТ АКТА. Заполните форму и сохраните данные
        </el-row>
        <form-acts
          v-if="response.id && actDialog"
          :response-id="response.id"
          :act-number="response.display_id"
          :bid-id="response.bid_id"
          :response-cost-amount="response.cost_amount"
          :response-deficiency-cost="response.deficiency_cost"
          :response-idle="response.idle"
          :response-idle-price="response.idle_price"
          :active-act="activeAct"
          :loading="loading"
          :ttn-data="response"
          :performer-name="performerName"
          :performer-id="performerId"
          @save="saveActForm"
          @update="updateActForm"
          @delete="onDelete"
          @nopaper="sendAct"
          v-on="$listeners"
        >
          <el-row v-if="notSigned" class="edm-btns">
            <el-button
              v-if="isEDMUser && isFiles"
              type="success"
              @click="sendToSign"
            >
              Пользователь в ЭДО. Отправить на подпись
            </el-button>
            <el-button type="success" @click="getPDFAndSend">
              Cделать PDF файл и положить на подпись
            </el-button>
          </el-row>
        </form-acts>
      </loader-wrapper>
    </template>
  </el-dialog>
</template>

<script>
import { RepositoryFactory } from '@/api/RepositoryFactory'
import { defaultDate, defaultDateRu } from '@/datas/DateFormats'
import { getDateOfCertainFormat } from '@/helpers/date'
import { mapActions, mapGetters } from 'vuex'
import FormActs from '@/components/Forms/FormActs'
import LoaderWrapper from '@/components/Shared/LoaderWrapper'

const FilesApi = RepositoryFactory.get('file')
const ApiDoc = RepositoryFactory.get('doc')
const EdmUsers = RepositoryFactory.get('edmUsers')
const Responses = RepositoryFactory.get('responses')
const NoPaperAPI = RepositoryFactory.get('noPaper')

export default {
  name: 'BidsActModal',
  components: {
    LoaderWrapper,
    FormActs,
  },
  props: {
    response: {
      type: Object,
      default: () => {},
    },
    activeAct: {
      type: Object,
      default: () => {},
    },
    docTypes: {
      type: Array,
      default: () => [],
    },
    actDialog: {
      type: Boolean,
      required: true,
    },
    loader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      uploadPercentage: 0,
      isEDMUser: null,
    }
  },
  computed: {
    ...mapGetters('organizations', ['isOrgs', 'getOrgById']),
    performerName() {
      return this.getOrgById(this.response.bid.org_performer_id)
    },
    performerId() {
      return this.response?.bid?.org_performer_id || 0
    },
    notSigned() {
      return (
        this.activeAct?.id &&
        (!this.activeAct.pc_api_transactions ||
          (this.activeAct.pc_api_transactions &&
            !this.activeAct.pc_api_transactions.length))
      )
    },
    isFiles() {
      return (
        this.activeAct && this.activeAct.files && this.activeAct.files.length
      )
    },
  },
  watch: {
    response(val) {
      if (this.actDialog) {
        if (!this.isOrgs) {
          this.fetchOrganizations({ can_be_performer: 1 })
        }
        if (val?.auto?.carrier_inn) {
          this.checkEDMUser(val.auto.carrier_inn)
        } else {
          this.isEDMUser = null
        }
      }
    },
  },
  methods: {
    ...mapActions('organizations', ['fetchOrganizations']),

    getPDFAndSend() {
      this.loading = true
      Responses.generateAct(this.response.id)
        .then(res => {
          const { status } = res

          if (status === 200) {
            this.$message.success('Документ успешно сгенерирован')
            this.$emit('update', this.response)
          }
        })
        .catch(e => this.errHandler(e))
        .finally(() => (this.loading = false))
    },
    sendToSign() {
      this.loading = true
      const { phone, organization_id } = this.isEDMUser
      const data = {
        api_user_phone: phone,
        api_user_organization_id: organization_id,
        type_code: 'works_act',
        rel_name: 'responses',
        rel_id: this.response.id,
      }

      ApiDoc.signDocument(data)
        .then(res => {
          const { status } = res

          if (status === 204) {
            this.$message.success('Документ успешно подписан')
            this.$emit('update', this.response)
          }
        })
        .catch(e => this.errHandler(e))
        .finally(() => (this.loading = false))
    },
    checkEDMUser(org_inn) {
      if (this.canAction('getEdmList')) {
        EdmUsers.getList({ org_inn })
          .then(res => {
            const { status, data } = res

            this.isEDMUser =
              status === 200 && data && data.length ? data[0] : null
          })
          .catch(e => this.errHandler(e))
      }
    },
    dayToFormat(date, flag = 0) {
      const format = flag ? defaultDateRu : defaultDate

      return date ? getDateOfCertainFormat(date, format) : ''
    },
    getFormData(data) {
      const formData = new FormData()

      for (let k in data) {
        if (k === 'date') {
          data[k] = this.dayToFormat(data[k])
        }
        formData.append(k, data[k])
      }

      return formData
    },
    async saveActForm(data) {
      const documentType = this.docTypes.find(t => t.code === 'works_act')

      if (!documentType) {
        return
      }
      const { file, ...document } = data
      this.loading = true
      const newDoc = {
        ...document,
        document_type_id: documentType.id,
        response_id: this.response.id,
        links: { responses: [this.response.id] },
      }
      // если изменились данные по оплате простоя - вначале обновляем сам response
      if (
        document.idle !== this.response.idle ||
        document.idle_price !== this.response.idle_price
      ) {
        const params = {
          idle: document.idle,
          idle_price: document.idle_price,
        }

        await Responses.updateResponse(
          this.response.id,
          params,
        )
      }

      ApiDoc.createActDocument(newDoc)
        .then(res => {
          if (file) {
            const formData = this.getFormData({
              file_path: file,
              document_id: res.data.id,
            })

            if (res.status === 201) {
              return FilesApi.upload(formData, this)
            }
          }

          return true
        })
        .then(() => {
          this.$message.success('АКТ успешно сохранен')
          this.$emit('update', this.response)
        })
        .catch(e => this.errHandler(e))
        .finally(() => (this.loading = false))
    },
    async updateActForm(data) {
      let docRes

      this.loading = true

      // если изменились данные по оплате простоя - вначале обновляем сам response
      if (
        data.idle !== this.response.idle ||
        data.idle_price !== this.response.idle_price
      ) {
        const params = {
          idle: data.idle,
          idle_price: data.idle_price,
        }

        try {
          await Responses.updateResponse(
              this.response.id,
              params,
          )
        } catch (error) {
          this.errHandler(error)
        }
      }
      try {
        docRes = await ApiDoc.updateActDocument(data)
      } catch (error) {
        this.errHandler(error)
      }

      if (docRes?.data?.id && data.file) {
        if (this.activeAct?.files[0]?.id) {
          try {
            await FilesApi.dellOne(this.activeAct.files[0].id)
          } catch (error) {
            this.errHandler(error)
          }
        }
        const formData = this.getFormData({
          file_path: data.file,
          document_id: docRes.data.id,
        })

        try {
          await FilesApi.upload(formData, this)
        } catch (error) {
          this.errHandler(error)
        }
        this.$emit('update', this.response)
      }
      if ([200, 201].includes(docRes?.status)) {
        this.$emit('update', this.response)
        this.$message.success('АКТ успешно обновлен')
      }
      this.loading = false
    },
    sendAct() {
      this.loading = true
      NoPaperAPI.sendAct({ response_id: this.response.id })
        .then(() => {
          this.$emit('status', this.response.id)
        })
        .catch(err => this.errHandler(err))
        .finally(() => (this.loading = false))
    },
    onDelete() {
      const { files } = this.activeAct

      if (files.length && files[0].id) {
        FilesApi.dellOne(files[0].id)
          .then(res => {
            if (res.status === 204) {
              this.$emit('update', this.response)
            }
          })
          .catch(e => this.errHandler(e))
      }
    },
  },
}
</script>

<style lang="sass">
.bids-act-modal
  *
    font-family: 'Rubik', sans-serif
  &__title
    margin-top: 0
    color: #000
    font-size: 18px
  .el-dialog__body
    font-size: 18px
    color: #000000
  .el-input__inner
    height: auto
  .ttn-labels .el-button
    font-size: 18px
  .plates
    font-size: 14px
    margin-bottom: 15px
    span:last-child
      margin-left: 10px
  .no-ttn
    color: #FF0000
    margin-bottom: 20px
  .add-ttn
    padding: 0
    border-bottom: 1px solid
    border-radius: 0
    &.el-button.is-disabled, &.el-button.is-disabled:focus, &.el-button.is-disabled:hover
      color: #000000
  .edm-btns
    display: flex
    flex-direction: column
    button
      margin: 15px 0 0
      font-size: 9.5px
      width: 260px
      text-transform: uppercase
</style>
